import { useMemo } from 'react';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { useCurrencyConversionRate, CurrencyRateData } from '@apps/card/hooks/useCurrencyConversionRate';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import { StationeryProduct } from '@shared/utils/print-price/types';

/**
 * Reusable hook that returns an instance of PrintPriceCalculator.
 *
 * @param products - Array of StationeryProduct instances
 * @param conversionRateOverride - Optional conversion rate to use instead of fetching from geo-location
 * @returns PrintPriceCalculator instance
 */
export const usePrintPrices = (products: StationeryProduct[], conversionRateOverride?: CurrencyRateData): PrintPriceCalculator => {
  const shippingMethodLabelsAndDescriptions = useShippingMethodLabelsAndDescriptions();
  const { currency } = useGeoBasedLocale();
  const { data: conversionRate } = useCurrencyConversionRate(currency);
  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');

  return useMemo(() => {
    // If conversionRateOverride is provided, use it; otherwise use the geo-location based rate
    const rateToUse = conversionRateOverride ?? (isLocalCurrencyEnabled ? conversionRate : undefined);
    return new PrintPriceCalculator(products, shippingMethodLabelsAndDescriptions, rateToUse);
  }, [products, shippingMethodLabelsAndDescriptions, isLocalCurrencyEnabled, conversionRate, conversionRateOverride]);
};
